import {SvipBadge} from '../../components/SvipBadge/SvipBadge';
import React from 'react';
import { colorChatText } from './ColoredChatText';
import useTranslator from '../../hooks/translate';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Tabs } from './ChatBox';
import { Message } from '../../slices/chatSlice';
import { toServer } from '../../util/ServerSocket';
import { ContextMenu } from '../../components/ContextMenu/ContextMenu';
import { useRef, useState } from 'react';
import { blockAccount } from '../../slices/characterSlice';
import { emojiMap } from './Emojis';
import { SystemMessage } from './SystemMessage';
import { CDNImage } from '../../components/Elements/Image';

const chatVillages: Record<number, { asset: string; name: string }> = {
  111: { asset: 'Leaf', name: 'หมู่บ้านไฟ' },
  211: { asset: 'Mist', name: 'หมู่บ้านหมอก' },
  311: { asset: 'Cloud', name: 'หมู่บ้านเมฆ' },
  411: { asset: 'Sand', name: 'หมู่บ้านทราย' },
  511: { asset: 'Rock', name: 'หมู่บ้านหิน' },
};

export const ChatDisplay: React.FC<{ tab: Tabs }> = ({ tab }) => {
  const global = useAppSelector((state) => state.chat.global);
  const party = useAppSelector((state) => state.chat.party);
  const system = useAppSelector((state) => state.chat.system);
  const talk = useAppSelector((state) => state.chat.talk);

  function onClick(accountId: number, serverId: number) {
    toServer('viewCharacter', { serverId, accountId });
  }

  if (tab === Tabs.GLOBAL) {
    return <>{compose(global, onClick, true)}</>;
  }
  if (tab === Tabs.SYSTEM) return <>{compose(system, onClick, true)}</>;
  if (tab === Tabs.TALK) return <>{compose(talk, onClick, true)}</>;

  if (tab === Tabs.PARTY) {
    return <>{compose(party, onClick)}</>;
  }

  return null;
};

function compose(messages: Message[], onClick: (id: number, serverId: number) => void, showServer = false) {
  return [...messages]
    .reverse()
    .map((message, idx) => message.kind === 'system'
      ? <SystemMessage key={idx} message={message} />
      : <ChatMessage key={idx} message={message} onClick={onClick} showServer={showServer} />);
}

function ChatMessage({
  message,
  onClick,
  showServer = false,
}: {
  message: Message;
  onClick: (id: number, serverId: number) => void;
  showServer: boolean;
}) {
  const dispatch = useAppDispatch();
  const t = useTranslator();

  const nameRef = useRef<HTMLSpanElement>(null);

  const myAccountId = useAppSelector((state) => state.account.id);

  const [showContextMenu, setShowContextMenu] = useState(false);
  const village = message.village ? chatVillages[message.village] : undefined;

  function replaceEmojis(text: string) {
    const lines = text.split(/:(\d+):/g).map((word, index) => (emojiMap[word] ? <img key={index} src={emojiMap[word]} title={word} /> : <React.Fragment key={index}>{colorChatText(word, message.nameColors)}</React.Fragment>));

    return lines;
  }

  return (
    <div onMouseLeave={() => setShowContextMenu(false)}>
      {message.serverId ? <span style={{ color: '#ffdf00' }}>[S{message.serverId.toString().padStart(2, '0')}] </span> : null}
      {village && <CDNImage src={`scenes/newPlayer/village${village.asset}.png`} alt={village.name} title={village.name} style={{ width: 18, height: 18, objectFit: 'contain', verticalAlign: 'middle', marginRight: 3 }} />}
      <span
        ref={nameRef}
        style={{ cursor: 'pointer', color: '#fff' }}
        onClick={() => onClick(message.id, message.serverId)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (message.id === myAccountId) {
            return;
          }

          setShowContextMenu(true);
        }}>
        {colorChatText(message.user, message.nameColors, message.nameNeon)}
      </span>
      {message.titleId ? <span style={{ color: '#ff4040' }}> [{t('title__' + message.titleId)}]</span> : null}
      {message.svip ? <SvipBadge small/> : message.vip && <img src="/assets/ui/UIResource/Icon/vip4.gif" alt="VIP" title="VIP" style={{ width: 24, height: 18, objectFit: 'contain', verticalAlign: 'middle', marginLeft: 3 }} />}
      <span style={{ color: '#fff' }}> : </span><span style={{ color: '#fff' }}>{replaceEmojis(message.message)} </span>
      {nameRef.current && showContextMenu && (
        <ContextMenu position={{ x: nameRef.current.getBoundingClientRect().right, y: nameRef.current.getBoundingClientRect().y - 15 }}>
          <ContextMenu.Item
            title="Block"
            onClick={() => {
              dispatch(blockAccount(message.id));
              toServer('blockAccount', message.id);
            }}
          />
        </ContextMenu>
      )}
    </div>
  );
}
